// The client-side repertoire of presentation directives Sim (the mission
// service inside Gaia) may send inline with a spoken line. See
// docs/glasses-client/10-server-directed-display-design.md and the Gaia
// server contract at features/mu-glasses-slice-server/PRESENTATION_DIRECTIVES_CONTRACT.md
// (Gaia repo, section 4).
//
// Allowed list (settled with the Gaia server team, 2026-09-28): Sim publishes
// the session's allowed display methods in experience.config as
// missionPresentationVocabulary (contract mission_presentation_vocabulary_v2);
// the allowed set is the `method` values across its intents. A directive is
// played only if its method is BOTH in that set (when received) AND in this
// client's repertoire below, and its params pass the rule for its method.
// PRESENTATION_DIRECTIVES_FIELD is the wire field name, kept in one place.
export const PRESENTATION_METHODS = [
  'presence.show',
  'display.text',
  'display.target',
  'display.map',
  'display.choice',
  'display.clear',
] as const;

export type PresentationMethod = (typeof PRESENTATION_METHODS)[number];

/** The wire field name on experience.utterance carrying the directive list. See the module note above. */
export const PRESENTATION_DIRECTIVES_FIELD = 'presentation_directives';

/** One directive, already parsed and validated against its method's parameter rule. */
export interface PresentationDirective {
  m: PresentationMethod;
  p: Record<string, unknown>;
  /** Milliseconds from the start of the line's playback (or its display, if voice is off). Absent = fire at line start. */
  offsetMs?: number;
  /** The vocabulary intent this directive came from, for logs and tests. */
  intentId?: string;
}

const VOCABULARY_V2 = 'mission_presentation_vocabulary_v2';

/**
 * The set of display methods a session's vocabulary allows: the `method`
 * values across `missionPresentationVocabulary.intents`. Returns null when the
 * vocabulary is absent, malformed, or not v2 (v1 cannot name display methods),
 * meaning "no session list; use the client repertoire alone".
 */
export function allowedMethodsFromVocabulary(raw: unknown): ReadonlySet<string> | null {
  if (!isRecord(raw) || raw.contractVersion !== VOCABULARY_V2 || !Array.isArray(raw.intents)) return null;
  const methods = new Set<string>();
  for (const intent of raw.intents) {
    if (isRecord(intent) && typeof intent.method === 'string') methods.add(intent.method);
  }
  return methods;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isPresentationMethod(value: unknown): value is PresentationMethod {
  return typeof value === 'string' && (PRESENTATION_METHODS as readonly string[]).includes(value);
}

/**
 * Tolerant parse of a raw `presentation_directives` array (a top-level field
 * on `experience.utterance`, sibling to `source`): not an array -> empty; an
 * entry that is not an object, or whose `m` is not a known method -> dropped
 * silently (an unrecognized method is not an error, just not in this
 * client's repertoire yet); a known method whose params fail its rule ->
 * dropped, with exactly one console.warn naming the method.
 */
export function parsePresentationDirectives(
  raw: unknown,
  warn: (message: string, detail?: unknown) => void = console.warn,
  allowed: ReadonlySet<string> | null = null
): PresentationDirective[] {
  if (!Array.isArray(raw)) return [];
  const result: PresentationDirective[] = [];
  for (const item of raw) {
    if (!isRecord(item)) continue;
    const method = item.m;
    if (!isPresentationMethod(method)) continue;
    if (allowed && !allowed.has(method)) continue;
    const params = isRecord(item.p) ? item.p : {};
    if (!isValidParams(method, params)) {
      warn(`GaiaExperienceWebSocket: presentation directive "${method}" has malformed params, dropping`, item);
      continue;
    }
    const offsetMs = item.offset_ms;
    result.push({
      m: method,
      p: params,
      offsetMs: typeof offsetMs === 'number' && offsetMs >= 0 ? offsetMs : undefined,
      intentId: typeof item.intent_id === 'string' ? item.intent_id : undefined,
    });
  }
  return result;
}

function isValidParams(method: PresentationMethod, params: Record<string, unknown>): boolean {
  switch (method) {
    case 'presence.show':
    case 'display.clear':
      // "none": any params at all make this malformed, not merely ignored.
      return Object.keys(params).length === 0;
    case 'display.text':
      return (
        typeof params.text === 'string' &&
        params.text.length > 0 &&
        params.text.length <= 80 &&
        (params.duration_ms === undefined || isInRange(params.duration_ms, 500, 10_000))
      );
    case 'display.target':
      return params.label === undefined || (typeof params.label === 'string' && params.label.length <= 40);
    case 'display.map': {
      const target = params.target;
      if (!isRecord(target)) return false;
      const validTarget =
        isInRange(target.lat, -90, 90) &&
        isInRange(target.lng, -180, 180) &&
        (target.label === undefined || typeof target.label === 'string') &&
        (target.arrival_radius_m === undefined || isInRange(target.arrival_radius_m, 1, 5000));
      const validView = params.view === undefined || params.view === 'compass' || params.view === 'map';
      return validTarget && validView;
    }
    case 'display.choice': {
      const options = params.options;
      return (
        Array.isArray(options) &&
        options.length >= 1 &&
        options.length <= 2 &&
        options.every((option) => typeof option === 'string' && option.length > 0 && option.length <= 24)
      );
    }
    default:
      return false;
  }
}

function isInRange(value: unknown, min: number, max: number): boolean {
  return typeof value === 'number' && value >= min && value <= max;
}

/** The presentation queue's current cue: one directive's method and params, or none. */
export interface DirectiveCue {
  method: PresentationMethod;
  params: Record<string, unknown>;
}

/** A cue that owns the card slot (replaces the line's text) rather than decorating it. */
export function ownsCardSlot(method: PresentationMethod): boolean {
  return method !== 'presence.show';
}

/** A cue that stays until something replaces it, rather than clearing on its own timer. */
function isPersistentCue(method: PresentationMethod): boolean {
  return method === 'display.map' || method === 'display.choice';
}

const PRESENCE_SHOW_MS = 1200;
const DEFAULT_CUE_MS = 3500;
const CUE_CAP_MS = 8000;

/**
 * How long a cue holds before clearing (or being followed by the next
 * scheduled directive). display.text and display.target use the toast rule
 * (build note #2) unless the directive gives its own duration_ms, in which
 * case that wins even past the 8s toast cap, since the server bounds it to
 * 10s. presence.show is a brief one-shot reveal. display.map and
 * display.choice are persistent: they stay until display.clear, the next
 * line, or (for display.choice) an answer is sent.
 */
function cueDurationMs(directive: PresentationDirective, toastDurationMs: (text: string) => number): number {
  const requestedMs = directive.p.duration_ms;
  if (typeof requestedMs === 'number' && requestedMs > 0) return requestedMs;
  if (directive.m === 'display.text' && typeof directive.p.text === 'string') {
    return Math.min(CUE_CAP_MS, toastDurationMs(directive.p.text));
  }
  if (directive.m === 'display.target') {
    const label = typeof directive.p.label === 'string' ? directive.p.label : '';
    return Math.min(CUE_CAP_MS, toastDurationMs(label || ' '));
  }
  if (directive.m === 'presence.show') return PRESENCE_SHOW_MS;
  return DEFAULT_CUE_MS;
}

export interface DirectiveCuePlayerPorts {
  setTimeout?: (run: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeout?: (handle: ReturnType<typeof setTimeout>) => void;
  /** Same duration rule the beat queue uses (beats.ts's toastDurationMs). Injectable for tests. */
  toastDurationMs?: (text: string) => number;
}

/**
 * Schedules a line's presentation_directives against its own timeline (zero =
 * the moment the line started playing/showing), one cue on screen at a time.
 * `offset_ms` is clamped to the line's length (build note "offset_ms ...
 * clamp to the line's length"). A later directive replaces whatever cue is
 * currently showing (one-cue-at-a-time). When a non-persistent cue's own
 * hold ends with nothing else scheduled after it, the player returns to
 * "no cue" (null) so the caller shows the line's own text again.
 */
export class DirectiveCuePlayer {
  private timers: ReturnType<typeof setTimeout>[] = [];
  private readonly listeners = new Set<() => void>();
  private snapshot: DirectiveCue | null = null;

  constructor(private readonly ports: DirectiveCuePlayerPorts = {}) {}

  get current(): DirectiveCue | null {
    return this.snapshot;
  }

  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  /** Cancels whatever was scheduled and begins scheduling this line's directives. */
  play(directives: readonly PresentationDirective[], lineDurationMs: number): void {
    this.cancelTimers();
    this.setSnapshot(null);
    if (directives.length === 0) return;
    const cappedLineDuration = Math.max(0, lineDurationMs);
    for (const directive of directives) {
      const fireAt = clamp(directive.offsetMs ?? 0, 0, cappedLineDuration);
      this.timers.push(this.schedule(() => this.fire(directive), fireAt));
    }
  }

  /** Clears the current cue and cancels anything still scheduled, without disposing the player. */
  cancel(): void {
    this.cancelTimers();
    this.setSnapshot(null);
  }

  dispose(): void {
    this.cancelTimers();
    this.listeners.clear();
  }

  private fire(directive: PresentationDirective): void {
    if (directive.m === 'display.clear') {
      this.setSnapshot(null);
      return;
    }
    this.setSnapshot({ method: directive.m, params: directive.p });
    if (isPersistentCue(directive.m)) return;
    const toastDurationMs = this.ports.toastDurationMs ?? defaultToastDurationMs;
    const holdMs = cueDurationMs(directive, toastDurationMs);
    this.timers.push(
      this.schedule(() => {
        this.setSnapshot(null);
      }, holdMs)
    );
  }

  private setSnapshot(cue: DirectiveCue | null): void {
    if (this.snapshot === cue) return;
    this.snapshot = cue;
    this.notify();
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener();
  }

  private cancelTimers(): void {
    for (const timer of this.timers) this.clear(timer);
    this.timers = [];
  }

  private schedule(run: () => void, ms: number): ReturnType<typeof setTimeout> {
    const scheduleFn = this.ports.setTimeout ?? ((fn: () => void, delay: number) => setTimeout(fn, delay));
    return scheduleFn(run, Math.max(0, ms));
  }

  private clear(handle: ReturnType<typeof setTimeout>): void {
    const cancelFn = this.ports.clearTimeout ?? ((h: ReturnType<typeof setTimeout>) => clearTimeout(h));
    cancelFn(handle);
  }
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Meta's toast rule, duplicated from beats.ts to avoid a circular import (beats.ts stays framework/queue-only). */
function defaultToastDurationMs(text: string): number {
  const wordCount = text.trim().split(/\s+/).filter(Boolean).length;
  const extraWords = Math.max(0, wordCount - 2);
  return Math.min(CUE_CAP_MS, 3500 + extraWords * 300);
}
